import React from 'react';
import { App, Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';

const LETTER_TYPES = [
  { value: 'salary_certificate', label: 'Salary certificate' },
  { value: 'experience_letter', label: 'Experience letter' },
  { value: 'employment_certificate', label: 'Employment certificate' },
];

const EssLettersPage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();

  const { data: rows = [], isFetching } = useQuery({
    queryKey: hrmsKeys.essLetterRequests(),
    queryFn: () => hrmsService.listEssLetterRequests(),
  });

  const createMut = useMutation({
    mutationFn: (body: object) => hrmsService.createEssLetterRequest(body),
    onSuccess: () => {
      message.success('Letter request submitted');
      form.resetFields();
      qc.invalidateQueries({ queryKey: hrmsKeys.essLetterRequests() });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Request failed'),
  });

  const download = async (id: string, number: string) => {
    try {
      const blob = await hrmsService.downloadEssLetterPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${number || 'letter'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Download failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Letter requests"
          subtitle="Salary, experience and employment certificates"
        />
      }
    >
      <Card title="New request" size="small" style={{ marginBottom: 16, maxWidth: 640 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ letter_type: 'salary_certificate', language: 'en' }}
        >
          <Form.Item name="letter_type" label="Letter type" rules={[{ required: true }]}>
            <Select options={LETTER_TYPES} />
          </Form.Item>
          <Form.Item name="addressed_to" label="Addressed to">
            <Input placeholder="To Whom It May Concern" />
          </Form.Item>
          <Form.Item name="purpose" label="Purpose">
            <Input.TextArea rows={2} placeholder="e.g. Bank loan application" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={createMut.isPending}>
            Submit request
          </Button>
        </Form>
      </Card>

      <Table
        rowKey="id"
        loading={isFetching}
        dataSource={rows}
        columns={[
          { title: 'Number', dataIndex: 'request_number', width: 140 },
          {
            title: 'Type',
            dataIndex: 'letter_type',
            render: (t: string) => LETTER_TYPES.find((x) => x.value === t)?.label || t,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
          },
          {
            title: 'Actions',
            width: 120,
            render: (_: unknown, r: any) =>
              r.status === 'Ready' ? (
                <Button size="small" icon={<DownloadOutlined />} onClick={() => download(r.id, r.request_number)}>
                  PDF
                </Button>
              ) : (
                '—'
              ),
          },
        ]}
      />
      <Space style={{ marginTop: 16 }}>
        <Button href="/hr/me">Back to My HRMS</Button>
      </Space>
    </WorkspaceLayout>
  );
};

export default EssLettersPage;
